import React, { useEffect, useContext, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Alert, Modal } from "react-bootstrap";
import { BsFillExclamationTriangleFill } from "react-icons/bs";
import {
    ALIGN,
    COHORT_TRAINING_FLAG,
    MIME_TYPES,
    POSITIONS,
    COHORT_MARKET_TYPE,
    US_STATE,
    ALERT_TYPE,
    FILE_TYPE,
    FILE_TYPE_VALUE,
    MEMBER_FILE_TYPE
} from "../../constants";
import SharedForm from "../../components/ui/Form";
import FileProgress from "../../components/ui/FileProgress";
import { isObjectEmpty, isValidName, optionValues, radioOptionValues } from "../../utils/utils";
import api from "../../services/api/memberAnalyticsService";
import messages from "../../utils/helper/messages";
import { AlertContext } from "../../context/AlertContext";

const NewCohortModal = ({ show, onCancel, onUpload, data }) => {
    const initialCohortState = {
        training: 'N',
        marketType: COHORT_MARKET_TYPE.Single,
        market: US_STATE.AL,
        documentUpload: '',
        file: null,
        systemFile: null,
        fileName: ''
    };
    const currentUser = useSelector((state) => state.user.currentUser);
    const { showAlert } = useContext(AlertContext);


    const allowedFileTypes = [MIME_TYPES.CSV];
    const trainingFlagOptions = useMemo(() => optionValues(COHORT_TRAINING_FLAG), []);
    const marketTypeOptions = useMemo(() => optionValues(COHORT_MARKET_TYPE), []);
    const marketOptions = useMemo(() => optionValues(US_STATE, true), []);

    const [isLoading, setIsLoading] = useState(false);
    const [cohortDetails, setCohortDetails] = useState(initialCohortState);
    const [tempMarket, setTempMarket] = useState(initialCohortState);
    const [selectedFileType, setSelectedFileType] = useState('');

    const [file, setFile] = useState("");
    const [progress, setProgress] = useState(0);
    const progressRef = useRef(null);

    const isMarketTypeMultiple = COHORT_MARKET_TYPE[cohortDetails.marketType] === COHORT_MARKET_TYPE.Multiple;

    useEffect(() => {
        if (data) {
            const updatedDetails = {
                ...initialCohortState,
                ...data,
                file: null,
                systemFile: null,
                fileName: '',
            };
            setCohortDetails(updatedDetails);
            setTempMarket(updatedDetails);
            setSelectedFileType(data.documentUpload || '');
        } else {
            setCohortDetails(initialCohortState);
            setTempMarket(initialCohortState);
            setSelectedFileType('');
        }
    }, [data]);

    useEffect(() => {
        if (data && cohortDetails?.documentUpload) {
            setSelectedFileType(cohortDetails.documentUpload);
        }
    }, [data, cohortDetails?.documentUpload]);


    const arr = useMemo(() => [
        { key: 'name', labelName: 'Cohort Name', placeholderName: 'Cohort Name', type: 'text',  maxLength: 32 },
        { key: 'training', labelName: 'Training Cohort', placeholderName: 'Training Cohort', type: 'options', optionValues: trainingFlagOptions, disabled: !!data },
        { key: 'marketType', labelName: 'Market Type', placeholderName: 'Market Type', type: 'options', optionValues: marketTypeOptions, disabled: !!data },
        {
            key: 'market', labelName: 'Market', placeholderName: 'Market',
            type: `${isMarketTypeMultiple ? 'text' : 'options'}`, optionValues: marketOptions,
            disabled: data ? false :isMarketTypeMultiple, maxLength: 2
        },
        {
            type: 'radio',
            key: 'documentUpload',
            labelName: 'Document Upload',
            optionValues: radioOptionValues(FILE_TYPE_VALUE, false, true),
            maxLength: 12,
            resetFields: ['file', 'systemFile'],
        },
        {
            key: 'file',
            type: 'file',
            allowedTypes: allowedFileTypes,
            fileNameKey: 'fileName',
            hidden: selectedFileType !== FILE_TYPE.Local,
            labelName: 'Local File',
            required: selectedFileType === FILE_TYPE.Local,
        },
        {
            key: 'systemFile',
            type: 'systemFile',
            allowedTypes: allowedFileTypes,
            fileNameKey: 'systemFileName',
            hidden: selectedFileType !== FILE_TYPE.System,
            labelName: 'System File',
            required: selectedFileType === FILE_TYPE.System,
            fileApi: () => api.GetCohortUpdateFiles(MEMBER_FILE_TYPE.COHORT)
        }
     ], [selectedFileType, isMarketTypeMultiple, data]);

    const Submit = async (formData) => {
        setIsLoading(true);

        const {
            file: localFile,
            systemFile,
            name,
            id,
            training,
            marketType,
            market
        } = cohortDetails;

        const { companyId, id: uploaderUserId } = currentUser;
        const isLocalFile = selectedFileType === FILE_TYPE.Local;
        const file = isLocalFile ? localFile : systemFile;

        if (!file) {
            showAlert(messages.error.noFileSelected, ALERT_TYPE.ERROR);
            setIsLoading(false);
            return;
        }

        const fileName = `${companyId}_${formData.name}_${file.name}`;
        const fileType = isLocalFile ? file.type : MIME_TYPES.CSV;

        const fileObj = new File([file], fileName, { type: fileType });

        Object.assign(fileObj, {
            companyId,
            uploaderUserId,
            cohortName: name,
            originalFileName: file.name,
            isLocalFile,
            ...(data ? { cohortId: id } : {
                training,
                marketType: COHORT_MARKET_TYPE[marketType],
                market
            })
        });

        const handleProgress = (event) => {
            progressRef.current = Math.round((100 * event.loaded) / event.total);
            setProgress(progressRef.current);
        };

        try {
            const uploadFile = data
                ? api.uploadFileEditCohort(fileObj, handleProgress)
                : api.uploadFile(fileObj, handleProgress);

            await uploadFile;

            const resetState = {
                ...initialCohortState,
                file: null,
                systemFile: null,
                fileName: '',
                documentUpload: ''
            };

            setCohortDetails(resetState);
            setTempMarket(resetState);
            setSelectedFileType('');
            setFile("");
            setProgress(0);
            setIsLoading(false);

            onUpload();
            showAlert(`${fileObj.originalFileName} ${messages.success.cohortUpload}`, ALERT_TYPE.SUCCESS);

        } catch (error) {
            setIsLoading(false);
            setProgress(0);
            showAlert(`${messages.error.cohortUpload} ${fileObj.originalFileName} ${error}.`, ALERT_TYPE.ERROR);
        }
    };

    const isValidCohortInfo = () => {
        if (isObjectEmpty(cohortDetails)) return false;

        const hasValidName = isValidName(cohortDetails.name);
        const hasFile = cohortDetails.file || cohortDetails.systemFile;

        return hasValidName && hasFile;
    };

    const handleCohortDetailsChange = (update) => {
        setCohortDetails((details) => ({ ...details, ...update }))
    }

    const onHideCallback = () => {
        setCohortDetails(initialCohortState);
        setFile("");
        setSelectedFileType('');
        onCancel();
    }

    const inputChange = (name, value) => {
        const update = {};

        switch (name) {
            case 'documentUpload':
                setSelectedFileType(value);
                Object.assign(update, { documentUpload: value, fileName: '', file: null, systemFile: null });
                break;
            case 'file':
                Object.assign(update, { fileName: value.name, file: value });
                setFile(value);
                break;
            case 'systemFile':
                Object.assign(update, { fileName: value.name, systemFile: value });
                setFile(value);
                break;
            case 'marketType':
                update[name] = value;
                if (COHORT_MARKET_TYPE[value] === COHORT_MARKET_TYPE.Multiple) {
                    setTempMarket(cohortDetails.market);
                    update.market = 'ZZ';
                } else {
                    update.market = tempMarket;
                }
                break;
            default:
                update[name] = value;
        }

        handleCohortDetailsChange(update);
    };

    return (
        <Modal show={show} fullscreen={false} backdrop="static" onHide={onHideCallback} size="md">
            <Modal.Header closeButton>
                <Modal.Title>{data ? 'Re-upload Cohort' : 'New Cohort'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-3 py-xl-2">
                    <SharedForm
                        cols={12}
                        array={arr}
                        modelObj={cohortDetails}
                        onSubmit={Submit}
                        onCancel={onHideCallback}
                        onInputChanged={inputChange}
                        loading={isLoading}
                        disabled={!isValidCohortInfo()}
                        actionBarPosition={POSITIONS.TOP}
                        actionBarAlign={ALIGN.RIGHT}
                    />
                    {progress > 0 && (
                        <FileProgress
                            file={file}
                            progress={progress}
                            wrapperStyle="mt-2"
                        />
                    )}
                </div>
                {
                    !isValidName(cohortDetails.name) &&
                    <div className="px-xl-3 mt-3">
                        <Alert
                            variant="warning"
                            className="py-2 lh-sm"
                        >
                            <div className="d-flex align-items-center">
                                <BsFillExclamationTriangleFill size={14} className="me-2 flex-shrink-0" />
                                {messages.error.cohortNameValidation}
                            </div>
                        </Alert>
                    </div>
                }
            </Modal.Body>
        </Modal>
    )
}

export default NewCohortModal;
